import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "../../../context/useGlobalState";
import styles from "./PromptVoiceInformation.module.css";
import { GENDER_OPTIONS, ACCENT_OPTIONS_BY_LANGUAGE, AGE_OPTIONS } from "./voiceCloneConstants";

interface PromptVoiceInformationProps {
    onBack: () => void;
    onSave: (
        name: string,
        description: string,
        gender?: string,
        accent?: string,
        age?: string,
        language?: string,
    ) => Promise<void>;
    isSaving?: boolean;
    defaultDescription?: string;
}

const PromptVoiceInformation: React.FC<PromptVoiceInformationProps> = ({
    onBack,
    onSave,
    isSaving = false,
    defaultDescription = "",
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const [voiceName, setVoiceName] = useState("");
    const [voiceDescription, setVoiceDescription] = useState(defaultDescription);
    const [gender, setGender] = useState("");
    const [accent, setAccent] = useState("");
    const [age, setAge] = useState("");

    // Map database language code to frontend code (for ElevenLabs)
    const dbToFrontendCode = (dbCode: string): string => {
        const mapping: Record<string, string> = {
            CN: "cn",
            KR: "kr",
            JP: "jp",
            GR: "gr",
        };
        return mapping[dbCode] || dbCode.toLowerCase();
    };

    // Get language options from data.languagesAI
    const languageOptions = useMemo(() => {
        return (data?.languagesAI || []).map((lang: any) => ({
            value: lang.code, // DB code (e.g., "FR", "CN")
            label: lang.label || lang.name,
        }));
    }, [data?.languagesAI]);

    // Initialize selectedLanguage with FR code or first available language
    const initialLanguage = useMemo(() => {
        const frLang = languageOptions.find((lang) => lang.value === "FR");
        return frLang?.value || languageOptions[0]?.value || "";
    }, [languageOptions]);

    const [selectedLanguage, setSelectedLanguage] = useState("");

    // Update selectedLanguage when initialLanguage is available
    useEffect(() => {
        if (initialLanguage && !selectedLanguage) {
            setSelectedLanguage(initialLanguage);
        }
    }, [initialLanguage, selectedLanguage]);

    // Get frontend code for accents lookup (e.g., "FR" -> "fr")
    const frontendLanguageCode = useMemo(() => {
        return dbToFrontendCode(selectedLanguage);
    }, [selectedLanguage]);

    const availableAccents = ACCENT_OPTIONS_BY_LANGUAGE[frontendLanguageCode] || [];

    const handleLanguageChange = (newLanguageCode: string) => {
        setSelectedLanguage(newLanguageCode);
        setAccent(""); // Reset accent when language changes
    };

    const handleSave = async () => {
        if (!voiceName.trim()) {
            alert(t("pleaseEnterVoiceName"));
            return;
        }

        // Convert DB code to frontend code for backend (e.g., "FR" -> "fr")
        const frontendCode = dbToFrontendCode(selectedLanguage);
        await onSave(voiceName, voiceDescription, gender, accent, age, frontendCode);
    };

    return (
        <div className={styles.voiceInformationForm}>
            <div className={styles.formGroup}>
                <label htmlFor="voiceName" className={styles.formLabel}>
                    {t("name")}
                </label>
                <input
                    id="voiceName"
                    type="text"
                    className={styles.formInput}
                    placeholder={t("voiceNamePlaceholder")}
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="voiceDescription" className={styles.formLabel}>
                    {t("description")}
                </label>
                <textarea
                    id="voiceDescription"
                    className={styles.formTextarea}
                    placeholder={t("voiceDescriptionPlaceholder")}
                    value={voiceDescription}
                    onChange={(e) => setVoiceDescription(e.target.value)}
                    rows={4}
                />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="voiceLanguage" className={styles.formLabel}>
                    {t("voiceLanguage")} ({t("optional")})
                </label>
                <select
                    id="voiceLanguage"
                    className={styles.formInput}
                    value={selectedLanguage}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                >
                    {languageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="gender" className={styles.formLabel}>
                    {t("gender")} ({t("optional")})
                </label>
                <select
                    id="gender"
                    className={styles.formInput}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                >
                    <option value="">{t("selectGender")}</option>
                    {GENDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="accent" className={styles.formLabel}>
                    {t("accent")} ({t("optional")})
                </label>
                <select
                    id="accent"
                    className={styles.formInput}
                    value={accent}
                    onChange={(e) => setAccent(e.target.value)}
                >
                    <option value="">{t("selectAccent")}</option>
                    {availableAccents.map((option) => (
                        <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="age" className={styles.formLabel}>
                    {t("age")} ({t("optional")})
                </label>
                <select id="age" className={styles.formInput} value={age} onChange={(e) => setAge(e.target.value)}>
                    <option value="">{t("selectAge")}</option>
                    {AGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formActions}>
                <button className={styles.backButton} onClick={onBack} disabled={isSaving}>
                    {t("back")}
                </button>
                <button className={styles.saveButton} onClick={handleSave} disabled={!voiceName.trim() || isSaving}>
                    {isSaving ? (
                        <>
                            <svg
                                className={styles.spinner}
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeDasharray="32"
                                    strokeDashoffset="32"
                                >
                                    <animate
                                        attributeName="stroke-dashoffset"
                                        values="32;0"
                                        dur="1s"
                                        repeatCount="indefinite"
                                    />
                                </circle>
                            </svg>
                            {t("saving")}...
                        </>
                    ) : (
                        t("saveVoice")
                    )}
                </button>
            </div>
        </div>
    );
};

export default PromptVoiceInformation;
